import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { Pod24AdminLayout } from "../../components/Pod24Admin/Pod24AdminLayout";
import OverviewTab, {
  OverviewKpiCards,
} from "../../components/Pod24Admin/CompanyDetail/OverviewTab";
import CompanyInfoTab from "../../components/Pod24Admin/CompanyDetail/CompanyInfoTab";
import RouterManagementTab from "../../components/Pod24Admin/CompanyDetail/RouterManagementTab";
import EquipmentStatusTab from "../../components/Pod24Admin/CompanyDetail/EquipmentStatusTab";
import CategoryContent from "../../components/Pod24Admin/CompanyDetail/CategoryContent";
import CategoryAddModal from "../../components/Pod24Admin/CompanyDetail/CategoryAddModal";
import { adminKpiApiService } from "../../../infrastructure/api/admin/admin-kpi-api-service";
import { adminApiService } from "../../../infrastructure/api/admin/admin-api-service";
import {
  PLUGIN_TYPE_LABELS,
  PluginType,
} from "../../../domain/types/kpi";
import type {
  CreatePluginRequest,
  Plugin,
} from "../../../domain/types/kpi";
import type { CompanyDetailResponse } from "../../../shared/types/api";

type StaticTab = "overview" | "company-info" | "routers" | "equipment-status";

// 카테고리 탭 식별용 slug. tab 파라미터 = `category-{index}`
const categoryTabValue = (index: number) => `category-${index}`;

/**
 * 사업자 등록번호 포맷: 10자리 숫자 → `XXX-XX-XXXXX` (3-2-5).
 * 자릿수가 다르거나 비어 있으면 원본/placeholder 그대로.
 */
const formatBusinessNumber = (raw?: string | null): string => {
  if (!raw) return "—";
  const digits = raw.replace(/\D/g, "");
  if (digits.length === 10) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
  }
  return raw;
};

/**
 * 한국 전화번호 포맷.
 * - 휴대폰(010·011·016~019): 11자리 → 3-4-4 / 10자리 → 3-3-4
 * - 서울(02): 10자리 → 2-4-4 / 9자리 → 2-3-4
 * - 지역(031~064, 3자리): 11자리 → 3-4-4 / 10자리 → 3-3-4
 * - 그 외는 원본 그대로.
 */
const formatPhone = (raw?: string | null): string => {
  if (!raw) return "—";
  const digits = raw.replace(/\D/g, "");

  // 휴대폰
  if (/^01[016-9]/.test(digits)) {
    if (digits.length === 11)
      return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
    if (digits.length === 10)
      return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  // 서울 02
  if (digits.startsWith("02")) {
    if (digits.length === 10)
      return `${digits.slice(0, 2)}-${digits.slice(2, 6)}-${digits.slice(6)}`;
    if (digits.length === 9)
      return `${digits.slice(0, 2)}-${digits.slice(2, 5)}-${digits.slice(5)}`;
  }
  // 지역 3자리 (031~064)
  if (/^0[3-6][0-9]/.test(digits)) {
    if (digits.length === 11)
      return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
    if (digits.length === 10)
      return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return raw;
};

// ─── 페이지 헤더 (개요/데이터보고 별로 다른 부제 + KPI 카드) ─────────────────
const PAGE_SUBTITLE: Record<StaticTab, string> = {
  overview:
    "자재 보유율·KPI 수집률·미해결 알람과 실시간 장비 현황을 한눈에 확인합니다.",
  "company-info":
    "업체 기본 정보·연락처와 소유자(회사 관리자) 정보를 확인합니다.",
  routers:
    "데이터 수집기(라우터)의 API 인증키를 발급하고 연결 정보를 확인합니다.",
  "equipment-status":
    "KPI 인증키 발급 시 등록된 대표 장비의 메타데이터와 수집 상태를 확인합니다.",
};

const PAGE_TITLE_SUFFIX: Record<StaticTab, string> = {
  overview: "개요",
  "company-info": "회사 정보",
  routers: "라우터 관리",
  "equipment-status": "장비 현황",
};

// 카테고리(사업 참여) 탭 부제 — 사업 코드별. 헤더 제목은 사업명(PLUGIN_TYPE_LABELS)으로 표기.
const CATEGORY_SUBTITLE: Record<PluginType, string> = {
  [PluginType.SMART_WORKSHOP]:
    "스마트제조 지원시스템 2.0 연동 기간·보고 대상 장비·KPI 슬롯과 보고 현황을 관리합니다.",
  [PluginType.SMART_FACTORY]:
    "MES 연동 기간·집계 대상 장비·KPI 슬롯을 관리합니다.",
  [PluginType.ACHIEVEMENT_NOTICE]:
    "회사 관리자 페이지 상단 배너에 노출되는 지원사업 고지 문구를 관리합니다.",
};

const ManageRegisteredCompanies2: React.FC = () => {
  const { companyId } = useParams<{ companyId: string }>();
  const companyIdNum = companyId ? Number(companyId) : null;
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // 회사 헤더 — GET /api/admin/company/{companyId} 응답 (CompanyDetailResponse)
  // 일부 필드(상태 뱃지, 최근 보고 시각)는 어드민 API 에서 제공되지 않아 placeholder.
  const [companyDetail, setCompanyDetail] = useState<CompanyDetailResponse | null>(
    null,
  );
  const [isLoadingCompany, setIsLoadingCompany] = useState(false);

  const loadCompanyDetail = useCallback(async () => {
    if (companyIdNum === null || Number.isNaN(companyIdNum)) return;
    setIsLoadingCompany(true);
    try {
      const response = await adminApiService.getCompanyInfo(companyIdNum);
      if (response.errorCode || !response.data) {
        throw new Error(response.errorMsg || "회사 정보 조회 실패");
      }
      setCompanyDetail(response.data);
    } catch (error) {
      console.error("회사 정보 조회 실패:", error);
      setCompanyDetail(null);
    } finally {
      setIsLoadingCompany(false);
    }
  }, [companyIdNum]);

  useEffect(() => {
    loadCompanyDetail();
  }, [loadCompanyDetail]);

  // 헤더 표시용 파생 데이터 — CompanyDetailResponse 가 없으면 placeholder ("—")
  const headerName = companyDetail?.company.companyName ?? "—";
  const headerCeoName = companyDetail?.company.ceoName?.trim() || "—";
  const headerBusinessNumber = formatBusinessNumber(
    companyDetail?.company.businessNumber,
  );
  const headerPhone = formatPhone(companyDetail?.company.phone);
  // 주소 = 기본주소 + 상세주소 (둘 다 없으면 "—")
  const headerAddress =
    [companyDetail?.company.address, companyDetail?.company.addressDetail]
      .filter((s) => s && s.trim())
      .join(" ")
      .trim() || "—";
  // 대표 로고 이미지(없으면 이니셜 폴백).
  const headerLogoUrl = companyDetail?.company.logoUrl?.trim() || null;
  // 쇼핑몰 URL — domain 이 있으면 로고 클릭 시 새 탭으로 이동.
  const headerDomain = companyDetail?.company.domain?.trim() || null;
  const headerShopUrl = headerDomain
    ? headerDomain.startsWith("http://") || headerDomain.startsWith("https://")
      ? headerDomain
      : `https://${headerDomain}`
    : null;
  // 헤더 로고 코드 = 회사명의 앞 2글자(공백 제거). 없으면 "—".
  const headerLogoCode =
    companyDetail?.company.companyName
      ?.replace(/\s+/g, "")
      .slice(0, 2)
      .toUpperCase() ?? "—";

  const [programs, setPrograms] = useState<Plugin[]>([]);
  const [isLoadingPrograms, setIsLoadingPrograms] = useState(false);

  const loadPrograms = useCallback(async (): Promise<Plugin[]> => {
    if (companyIdNum === null || Number.isNaN(companyIdNum)) return [];
    setIsLoadingPrograms(true);
    try {
      const response = await adminKpiApiService.getPluginsByCompany(companyIdNum);
      if (response.errorCode) {
        throw new Error(response.errorMsg || "카테고리 조회 실패");
      }
      const list = response.data ?? [];
      setPrograms(list);
      return list;
    } catch (error) {
      console.error("카테고리 조회 실패:", error);
      setPrograms([]);
      return [];
    } finally {
      setIsLoadingPrograms(false);
    }
  }, [companyIdNum]);

  useEffect(() => {
    loadPrograms();
  }, [loadPrograms]);

  // 현재 활성 탭 (?tab= 보존)
  const tabParam = searchParams.get("tab") ?? "overview";

  // 카테고리 탭 인덱스 (활성 탭이 category-N 인 경우)
  const activeCategoryIndex = useMemo(() => {
    const m = tabParam.match(/^category-(\d+)$/);
    if (!m) return null;
    const idx = Number(m[1]);
    return idx >= 0 && idx < programs.length ? idx : null;
  }, [tabParam, programs.length]);

  // 정적 탭 판정 (카테고리 탭이 아닌 경우)
  const activeStaticTab: StaticTab = useMemo(() => {
    if (activeCategoryIndex !== null) return "equipment-status"; // 헤더 표기상 기본
    if (tabParam === "company-info") return "company-info";
    if (tabParam === "routers") return "routers";
    if (tabParam === "equipment-status") return "equipment-status";
    return "overview";
  }, [tabParam, activeCategoryIndex]);

  const setTab = useCallback(
    (value: string) => {
      const next = new URLSearchParams(searchParams);
      next.set("tab", value);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  // 만약 ?tab=category-N 인데 해당 카테고리가 사라지면 overview 로 복귀.
  // 단, 아직 로딩 중일 때는 탭을 옮기지 않는다 (초기 로드 직후 강제 이동 방지).
  useEffect(() => {
    if (isLoadingPrograms) return;
    const m = tabParam.match(/^category-(\d+)$/);
    if (m) {
      const idx = Number(m[1]);
      if (idx < 0 || idx >= programs.length) {
        setTab("overview");
      }
    }
  }, [tabParam, programs.length, setTab, isLoadingPrograms]);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const handleCategorySubmit = async (
    request: Omit<CreatePluginRequest, "companyId">,
  ) => {
    if (companyIdNum === null || Number.isNaN(companyIdNum)) return;
    try {
      const response = await adminKpiApiService.createPlugin({
        ...request,
        companyId: companyIdNum,
      });
      if (response.errorCode || !response.data) {
        throw new Error(response.errorMsg || "카테고리 등록 실패");
      }
      // 목록 갱신 후, 방금 만든 program 의 인덱스로 이동
      const refreshed = await loadPrograms();
      setIsAddModalOpen(false);
      const createdId = response.data.id;
      const createdIdx = refreshed.findIndex((p) => p.id === createdId);
      if (createdIdx >= 0) {
        setTab(categoryTabValue(createdIdx));
      } else if (refreshed.length > 0) {
        // fallback: 백엔드가 새 항목을 마지막에 두는 경우
        setTab(categoryTabValue(refreshed.length - 1));
      }
    } catch (error) {
      console.error("카테고리 등록 실패:", error);
      const message =
        error instanceof Error ? error.message : "카테고리 등록에 실패했습니다.";
      alert(message);
    }
  };

  const handleRemoveCategory = async (index: number) => {
    const target = programs[index];
    if (!target) return;
    try {
      const response = await adminKpiApiService.deletePlugin(target.id);
      if (response.errorCode) {
        throw new Error(response.errorMsg || "카테고리 삭제 실패");
      }
      await loadPrograms();
      setTab("equipment-status");
    } catch (error) {
      console.error("카테고리 삭제 실패:", error);
      const message =
        error instanceof Error ? error.message : "카테고리 삭제에 실패했습니다.";
      alert(message);
    }
  };

  const handleProgramUpdated = useCallback(() => {
    loadPrograms();
  }, [loadPrograms]);

  // 헤더 제목·부제 — 카테고리 탭이면 사업명/사업 부제, 아니면 정적 탭별.
  const activeProgram =
    activeCategoryIndex !== null ? programs[activeCategoryIndex] : null;
  const headerTitleSuffix = activeProgram
    ? PLUGIN_TYPE_LABELS[activeProgram.pluginType]
    : PAGE_TITLE_SUFFIX[activeStaticTab];
  const headerSubtitle = activeProgram
    ? CATEGORY_SUBTITLE[activeProgram.pluginType]
    : PAGE_SUBTITLE[activeStaticTab];

  return (
    <Pod24AdminLayout>
      <div
        className="text-[#252525]"
        style={{ fontFamily: "Pretendard, -apple-system, BlinkMacSystemFont, sans-serif" }}
      >
        {/* 목록으로 돌아가기 */}
        <button
          type="button"
          onClick={() => navigate("/admin/company/manage")}
          className="inline-flex items-center gap-1 text-[13px] text-[#555] hover:text-[#252525] mb-4"
        >
          ← 등록 업체 관리
        </button>

        {/* 페이지 헤더 */}
        <div className="mb-[28px] flex flex-col items-start gap-2">
          <h2 className="text-[1.375rem] font-semibold">
            {isLoadingCompany && !companyDetail ? "로딩 중…" : headerName} ·{" "}
            {headerTitleSuffix}
          </h2>
          <span className="text-[1rem] font-normal text-[#555]">
            {headerSubtitle}
          </span>
        </div>

        {/* 회사 정보 카드 */}
        <section className="border border-[#D9D9D9] rounded-[0.625rem] bg-white p-6 mb-4">
          <div className="flex items-center gap-8">
            {/* 로고 + 회사명 (로고 위, 이름 아래) — 로고 클릭 시 쇼핑몰 새 탭 이동 */}
            <div className="flex-shrink-0 flex flex-col items-center gap-2 w-[100px]">
              {(() => {
                const logoBox = (
                  <div
                    className={`w-[88px] h-[60px] flex items-center justify-center overflow-hidden ${
                      headerShopUrl ? "hover:opacity-80 transition-opacity" : ""
                    }`}
                    style={{
                      background: "#F7F8FA",
                      border: "1px solid #ECEDF1",
                      borderRadius: 8,
                    }}
                  >
                    {headerLogoUrl ? (
                      <img
                        src={headerLogoUrl}
                        alt={headerName}
                        className="max-w-full max-h-full object-contain p-1.5"
                      />
                    ) : (
                      <div className="text-[12px] font-bold text-center" style={{ color: "#1A4FA5" }}>
                        {headerLogoCode}
                      </div>
                    )}
                  </div>
                );
                return headerShopUrl ? (
                  <a
                    href={headerShopUrl}
                    target="_blank"
                    rel="noreferrer"
                    title={`쇼핑몰 열기 (${headerDomain})`}
                  >
                    {logoBox}
                  </a>
                ) : (
                  logoBox
                );
              })()}
              <h1 className="text-[15px] font-bold text-center leading-tight break-keep">
                {headerName}
              </h1>
            </div>
            {/* 대표자명 ~ 주소 한 줄 — 4분할 균등 분포(전체 폭 고르게) + 좌우 패딩 */}
            <div className="flex-1 min-w-0 grid grid-cols-4 gap-x-6 px-6 text-[12.5px]">
              <div className="min-w-0">
                <span className="text-[#888] text-[11px]">대표자명</span>
                <div className="font-medium">{headerCeoName}</div>
              </div>
              <div className="min-w-0">
                <span className="text-[#888] text-[11px]">사업자 등록 번호</span>
                <div className="font-medium">{headerBusinessNumber}</div>
              </div>
              <div className="min-w-0">
                <span className="text-[#888] text-[11px]">대표 연락처</span>
                <div className="font-medium">{headerPhone}</div>
              </div>
              <div className="min-w-0">
                <span className="text-[#888] text-[11px]">주소</span>
                <div className="font-medium">{headerAddress}</div>
              </div>
            </div>
          </div>
        </section>

        {/* 탭별 요약 카드 (개요 헤더 영역) */}
        {activeCategoryIndex === null && activeStaticTab === "overview" && (
          <OverviewKpiCards companyId={companyIdNum} />
        )}

        {/* 탭 네비게이션 + 본문 */}
        <section className="bg-white rounded-[0.625rem] border border-[#D9D9D9]">
          <div className="flex items-center gap-7 px-5 border-b border-[#D9D9D9] overflow-x-auto">
            <TabButton
              label="개요"
              active={activeCategoryIndex === null && activeStaticTab === "overview"}
              onClick={() => setTab("overview")}
            />
            <TabButton
              label="회사 정보"
              active={activeCategoryIndex === null && activeStaticTab === "company-info"}
              onClick={() => setTab("company-info")}
            />
            <TabButton
              label="라우터 관리"
              active={activeCategoryIndex === null && activeStaticTab === "routers"}
              onClick={() => setTab("routers")}
            />
            <TabButton
              label="장비 현황"
              active={activeCategoryIndex === null && activeStaticTab === "equipment-status"}
              onClick={() => setTab("equipment-status")}
            />

            {/* 동적 카테고리 탭 */}
            {programs.map((program, idx) => (
              <TabButton
                key={`program-${program.id}`}
                label={PLUGIN_TYPE_LABELS[program.pluginType]}
                active={activeCategoryIndex === idx}
                onClick={() => setTab(categoryTabValue(idx))}
              />
            ))}

            {/* ＋ 카테고리 추가 버튼 — 모든 사업(공방·공장·고지)이 등록되면 추가할 게 없으므로 숨김 */}
            {[
              PluginType.SMART_WORKSHOP,
              PluginType.SMART_FACTORY,
              PluginType.ACHIEVEMENT_NOTICE,
            ].some((c) => !programs.some((p) => p.pluginType === c)) && (
              <button
                onClick={() => setIsAddModalOpen(true)}
                title="카테고리 추가"
                className="my-2 ml-1 w-7 h-7 flex-shrink-0 text-[14px] text-[#4285F4] hover:bg-[#F5F8FF] rounded-md flex items-center justify-center"
                style={{ border: "1px dashed rgba(66,133,244,0.5)" }}
              >
                ＋
              </button>
            )}
          </div>

          {/* 본문 */}
          {activeCategoryIndex !== null && companyIdNum !== null ? (
            <CategoryContent
              key={programs[activeCategoryIndex].id}
              program={programs[activeCategoryIndex]}
              companyId={companyIdNum}
              onRemove={() => handleRemoveCategory(activeCategoryIndex)}
              onProgramUpdated={handleProgramUpdated}
            />
          ) : activeStaticTab === "overview" ? (
            <OverviewTab companyId={companyIdNum} onNavigate={setTab} />
          ) : activeStaticTab === "company-info" ? (
            <CompanyInfoTab detail={companyDetail} onUpdated={loadCompanyDetail} />
          ) : activeStaticTab === "routers" ? (
            <RouterManagementTab companyId={companyIdNum} />
          ) : (
            <EquipmentStatusTab companyId={companyIdNum} />
          )}
        </section>
      </div>

      {isAddModalOpen && (
        <CategoryAddModal
          existingProgramCodes={programs.map((p) => p.pluginType)}
          onClose={() => setIsAddModalOpen(false)}
          onSubmit={handleCategorySubmit}
        />
      )}
    </Pod24AdminLayout>
  );
};

// ─── 탭 버튼 ──────────────────────────────────────────────────────────────────
const TabButton: React.FC<{
  label: string;
  active: boolean;
  onClick: () => void;
}> = ({ label, active, onClick }) => (
  <button
    onClick={onClick}
    className={`py-3 text-[14px] whitespace-nowrap flex-shrink-0 ${
      active ? "font-semibold text-[#252525]" : "text-[#888]"
    }`}
    style={active ? { borderBottom: "2px solid #4285F4" } : undefined}
  >
    {label}
  </button>
);

export default ManageRegisteredCompanies2;